import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Typography,
  Box
} from '@mui/material';
import { useReportContext } from '../../contexts/ReportContext';
import { ReportTabType } from '../../contexts/ReportContext';

interface ReportFilterProps {
  reportType: ReportTabType;
}

// 現在の会計年度を計算する関数（3月始まり2月締め）
const getCurrentFiscalYear = (): number => {
  const today = new Date();
  const month = today.getMonth() + 1; // JavaScriptの月は0始まり
  const year = today.getFullYear();
  
  // 1月と2月は前年度の会計年度に属する
  return month <= 2 ? year - 1 : year;
};

// 会計年度の文字列表現を取得
const getFiscalYearLabel = (fiscalYear: number): string => {
  return `${fiscalYear}年度 (${fiscalYear}.3～${fiscalYear + 1}.2)`;
};

// 会計年度の開始月と終了月を計算
const getFiscalYearRange = (startYear: number, endYear: number): { startDate: string, endDate: string } => {
  return {
    startDate: `${startYear}-03`,
    endDate: `${endYear + 1}-02` // 終了年度の翌年2月まで
  };
};

export const ReportFilter: React.FC<ReportFilterProps> = ({
  reportType
}) => {
  // ReportContextから必要なデータと関数を取得
  const {
    getTabFilterParams,
    updateFilterParams,
    triggerReportDisplay
  } = useReportContext();

  // 現在のタブのフィルターパラメータを取得
  const filterParams = getTabFilterParams(reportType);

  // 会社トレンドレポート用の状態
  const currentYear = getCurrentFiscalYear();
  const [startFiscalYear, setStartFiscalYear] = useState(currentYear);
  const [endFiscalYear, setEndFiscalYear] = useState(currentYear);
  
  // 会計年度の初期値を設定（会社トレンドレポートのみ）
  useEffect(() => {
    if (reportType === 'company-trend') {
      // フィルターパラメータから会計年度を復元
      if (filterParams.startDate && filterParams.startDate.length === 7) {
        const startYear = parseInt(filterParams.startDate.substring(0, 4));
        setStartFiscalYear(startYear === currentYear + 1 ? currentYear : startYear);
      }
      
      if (filterParams.endDate && filterParams.endDate.length === 7) {
        const endYear = parseInt(filterParams.endDate.substring(0, 4)) - 1;
        setEndFiscalYear(endYear);
      }
    }
  }, [reportType]);
  
  // 会計年度が変更されたら日付範囲を更新（会社トレンドレポートのみ）
  useEffect(() => {
    if (reportType === 'company-trend') {
      const dateRange = getFiscalYearRange(startFiscalYear, endFiscalYear);
      updateFilterParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
    }
  }, [startFiscalYear, endFiscalYear, reportType]);

  // フィルター適用ハンドラー
  const handleApply = () => {
    triggerReportDisplay();
  };
  
  // 会計年度のオプションを生成（過去5年分＋現在＋次年度）
  const fiscalYearOptions = [];
  const currentFiscalYear = getCurrentFiscalYear();
  for (let i = -5; i <= 1; i++) {
    fiscalYearOptions.push(currentFiscalYear + i);
  }

  // 終了年度が開始年度より前にならないようにする
  const handleStartYearChange = (newYear: number) => {
    setStartFiscalYear(newYear);
    if (newYear > endFiscalYear) {
      setEndFiscalYear(newYear);
    }
  };

  // 開始年度が終了年度より後にならないようにする
  const handleEndYearChange = (newYear: number) => {
    setEndFiscalYear(newYear);
  };

  // 3年以上の範囲を選択した場合は集計単位を調整（会社トレンドレポートのみ）
  useEffect(() => {
    if (reportType === 'company-trend') {
      const yearDiff = endFiscalYear - startFiscalYear;
      if (yearDiff >= 3 && filterParams.unit === 'monthly') {
        // 3年以上の範囲では月次は細かすぎるため、自動的に四半期に切り替え
        updateFilterParams({ unit: 'quarterly' });
      }
    }
  }, [startFiscalYear, endFiscalYear, reportType]);

  // 会社トレンドレポート用のフィルター
  if (reportType === 'company-trend') {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          レポート条件
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          {/* 開始会計年度選択 */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>開始会計年度</InputLabel>
              <Select
                value={startFiscalYear}
                onChange={(e) => handleStartYearChange(Number(e.target.value))}
                label="開始会計年度"
              >
                {fiscalYearOptions.map(year => (
                  <MenuItem key={`start-${year}`} value={year}>
                    {getFiscalYearLabel(year)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* 終了会計年度選択 */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>終了会計年度</InputLabel>
              <Select
                value={endFiscalYear}
                onChange={(e) => handleEndYearChange(Number(e.target.value))}
                label="終了会計年度"
                disabled={fiscalYearOptions.filter(y => y >= startFiscalYear).length <= 1}
              >
                {fiscalYearOptions
                  .filter(year => year >= startFiscalYear)
                  .map(year => (
                    <MenuItem key={`end-${year}`} value={year}>
                      {getFiscalYearLabel(year)}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          
          {/* 選択された期間の表示 */}
          <Grid item xs={12} sm={12} md={6}>
            <Box sx={{ border: '1px solid #e0e0e0', p: 1, borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                対象期間: {filterParams.startDate}～{filterParams.endDate}
                {endFiscalYear - startFiscalYear > 0 && (
                  <span> （{endFiscalYear - startFiscalYear + 1}年分）</span>
                )}
              </Typography>
            </Box>
          </Grid>
          
          {/* 集計単位選択 */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>集計単位</InputLabel>
              <Select
                value={filterParams.unit || 'quarterly'}
                onChange={(e) => {
                  updateFilterParams({ unit: e.target.value as any });
                }}
                label="集計単位"
              >
                {/* 3年以上の範囲では月次は選択不可 */}
                <MenuItem 
                  value="monthly" 
                  disabled={endFiscalYear - startFiscalYear >= 3}
                >
                  月次
                  {endFiscalYear - startFiscalYear >= 3 && " (長期間のため選択不可)"}
                </MenuItem>
                <MenuItem value="quarterly">四半期</MenuItem>
                <MenuItem value="half">半期</MenuItem>
                <MenuItem value="yearly">年次</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* 表示ボタン */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              fullWidth
            >
              レポート表示
            </Button>
          </Grid>
          
          {/* クリアボタン */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setStartFiscalYear(currentFiscalYear);
                setEndFiscalYear(currentFiscalYear);
                updateFilterParams({ unit: 'quarterly' });
              }}
              fullWidth
            >
              条件リセット
            </Button>
          </Grid>
          
          {/* 集計単位の説明 */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
              ※集計単位の説明:
              「月次」: 各月のデータを個別に表示（最大2年分）
              「四半期」: 第1四半期(3-5月)、第2四半期(6-8月)、第3四半期(9-11月)、第4四半期(12-2月)で集計
              「半期」: 上期(3-8月)、下期(9-2月)で集計
              「年次」: 会計年度全体(3月-翌2月)で集計
            </Typography>
          </Grid>
          
          {/* 長期間選択時の注意書き */}
          {endFiscalYear - startFiscalYear >= 2 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                ※長期間のデータを表示する場合、レポートの読み込みに時間がかかる場合があります。
                {endFiscalYear - startFiscalYear >= 3 && " 3年以上の期間では月次表示はできません。"}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  }
  
  // その他のレポートタイプ用のフィルター
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        レポート条件
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="開始月"
            type="month"
            value={filterParams.startDate}
            onChange={(e) => updateFilterParams({ startDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="終了月"
            type="month"
            value={filterParams.endDate}
            onChange={(e) => updateFilterParams({ endDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            fullWidth
          >
            レポート表示
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};